"use client"
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import CreateAvatarDialog from './CreateAvatarDialog';
import { useAuth } from '../../useAuth';
import { useOrganization } from '../../contexts/OrganizationContext';
import useToast from "../../hooks/useToast";
import {
  Plus,
  LayoutGrid,
  List as LayoutList,
  X,
  Loader,
  Image,
  Search,
  Filter,
  Sparkles,
  TrendingUp,
  XCircle,
  Globe,
  Code2,
  Users,
  Languages
} from "lucide-react"
import { Button } from "../ui/button"
import ToastContainer from "../ui/ToastContainer"
import ConfirmDialog from "../ui/ConfirmDialog"
import TokenService from '../../services/token.service';
import { getTemplateLanguageLabel } from '../../types/template.types';

import { StatTile } from "../dashboard/ResourceBits"
import templateService from "../../services/template.service"
import AvatarCard, { MagicHoverWrapper } from "./AvatarCard"
import AvatarDetail from "./AvatarDetail"
import SectionModal from "./SectionModal"
import {
  fieldsToJsonSchema,
  jsonSchemaOf,
  jsonSchemaToFields,
  normalizeOutputMode,
  normalizeTag,
  outputFormatOf,
  splitTemplateSections,
  toSectionsPayload,
} from "./lib/avatarMapper"

// How many avatars we pull per request while scrolling.
const PAGE_SIZE = 24;

export default function AvatarsView({
  projectId,
  launchCreateMode = false,
  onBackToAssistantCreate,
  onTemplateCreated,
  hideTitle = false,
}) {
  const { user } = useAuth();
  const { currentAccount } = useOrganization();
  const accountId = currentAccount?.id || user?.account_id || user?.accountId || user?.sub;

  const loadTemplatesInFlightRef = useRef(null);

  // Toast notifications
  const { toasts, removeToast, success, error, info } = useToast();

  // Deep-link: ?templateId=<id> auto-opens that avatar (e.g. from a Zeno card
  // opened in a new tab). Guarded so closing the detail doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams()
  // Which avatar the URL currently has open, so we only react to real changes.
  const openedIdRef = useRef(null)
  const [viewMode, setViewMode] = useState("grid")
  // Filtro: "all" (todos), "public" (públicos), "mine" (mis avatares)
  const [filter, setFilter] = useState("all");
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [isEditModeActive, setIsEditModeActive] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  
  // Estados para búsqueda y filtros
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedFilterTags, setSelectedFilterTags] = useState([])
  const [showTrendingTags, setShowTrendingTags] = useState(true)
  
  // Confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState(null)

  // Modal para ver section completa
  const [expandedSection, setExpandedSection] = useState(null)

  // Templates data — loaded incrementally as the user scrolls (see loadMoreTemplates).
  const [templates, setTemplates] = useState([])
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  // Form state
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [isDefault, setIsDefault] = useState(false)
  const [isPublic, setIsPublic] = useState(false)
  const [examples, setExamples] = useState([])
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState("")
  const [avatarBase64, setAvatarBase64] = useState("")
  const [avatarUrl, setAvatarUrl] = useState("")
  const [language, setLanguage] = useState("es")
  const [outputFormat, setOutputFormat] = useState("normal")
  const [jsonSchema, setJsonSchema] = useState("")
  const [jsonSchemaFields, setJsonSchemaFields] = useState([])
  const [jsonSchemaBuilderMode, setJsonSchemaBuilderMode] = useState(true) // true = builder, false = raw JSON
  
  // Sections state (using SectionsManager)
  const [sections, setSections] = useState([])
  const [projects, setProjects] = useState([])
  const [projectsError, setProjectsError] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([])

  // Validation states
  const [validationErrors, setValidationErrors] = useState({
    name: false,
    sections: false,
    projects: false
  });

  // Callbacks memoizados para pasar a AvatarCard
  const handleCopyId = useCallback((id) => {
    navigator.clipboard.writeText(id);
    info('Copied', 'Template ID copied to clipboard');
  }, [info]);

  const handleCopyText = useCallback((text) => {
    navigator.clipboard.writeText(text || '');
    info('Copied', 'Content copied to clipboard');
  }, [info]);



  /**
   * Manejar selección de template para editar (optimizado para transición smooth)
   */
  /** Hydrate the output-format fields off a template, in either casing. */
  const applyOutputFormat = useCallback((source) => {
    setOutputFormat(
      normalizeOutputMode(source.outputFormat?.mode || source.output_format?.mode)
    );

    const schemaData = jsonSchemaOf(source);
    if (schemaData) {
      setJsonSchema(JSON.stringify(schemaData, null, 2));
      setJsonSchemaFields(jsonSchemaToFields(schemaData));
      setJsonSchemaBuilderMode(true); // Default to builder mode when loading schema
    } else {
      setJsonSchema('');
      setJsonSchemaFields([]);
    }
  }, []);

  /**
   * Show a template in the detail view.
   *
   * Not called directly from the UI — clicking a card only writes the id to the
   * URL, and the effect below calls this. That keeps one path into the detail
   * view whether you clicked, pasted a link, or hit the browser's back button.
   */
  const openTemplate = useCallback(async (template) => {
    // Scroll inmediato para feedback visual
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Abrir vista de detalle (NO modo edición) INMEDIATAMENTE con datos básicos disponibles
    setEditingTemplate(template);
    setEditing(true);
    setIsEditModeActive(false);

    // Poblar con datos básicos del cache (lista) inmediatamente
    setName(template.name || "");
    setDescription(template.description || "");
    setIsDefault(template.isDefault || template.is_default || false);
    setIsPublic(template.isPublic || template.is_public || false);
    setTags(template.tags || []);
    setAvatarBase64("");
    setAvatarUrl(template.avatar || "");
    setLanguage(template.language || "es");
    applyOutputFormat(template);

    // Cargar datos completos en segundo plano (sections, examples)
    try {
      const result = await templateService.getTemplate(template.id);

      if (result.success) {
        const templateData = result.data;

        // Blocks and examples come back as one array, split by section_type.
        const { sections: sectionsData, examples: examplesData } =
          splitTemplateSections(templateData.sections);

        setSections(sectionsData);
        setExamples(examplesData);
        setSelectedProjects(templateData.sharedProjects || []);
        setLanguage(templateData.language || "es");
        applyOutputFormat(templateData);
        setEditingTemplate(templateData);
      } else {
        console.error('❌ Error obteniendo template:', result.error);
        error('Load Failed', result.error.message || 'Failed to load complete template data');
      }
    } catch (err) {
      console.error('Error inesperado:', err);
      error('Unexpected Error', 'An unexpected error occurred while loading the template');
    }
  }, [error, applyOutputFormat]);

  /** Leave the detail view. State only — the URL is handled by the caller. */
  const closeDetail = useCallback(() => {
    window.scrollTo(0, 0);
    setEditing(false);
    setIsEditModeActive(false);
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Clicking a card only moves the URL; the effect below does the opening.
   *
   * No se toca `view`: los avatares son una pestaña dentro de Agents, así que
   * la vista ya es la correcta y escribir `view=Avatars` —el nombre viejo—
   * solo disparaba la reescritura de enlaces viejos en cada clic.
   */
  const handleSelectTemplate = useCallback((template) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('templateId', template.id);
      return next;
    });
  }, [setSearchParams]);

  /**
   * The URL owns which avatar is open.
   *
   * `?templateId=<id>` → show it (from the loaded list if we have it, else
   * fetch that one). No id → we're back on the list. This is what makes the
   * browser's back button and a pasted link behave the same as a click.
   */
  useEffect(() => {
    if (creating) return;
    const tid = searchParams.get('templateId');

    if (tid && openedIdRef.current !== tid) {
      const match = templates.find((t) => String(t.id) === String(tid));
      if (match) {
        openedIdRef.current = tid;
        openTemplate(match);
        return;
      }
      // La lista todavía no llegó, así que no se sabe si el avatar está en
      // ella. No se decide nada: se vuelve a intentar cuando cargue.
      //
      // Antes el id se marcaba como atendido aquí arriba, antes de mirar nada.
      // Entrar directo por URL —una tarjeta de Zeno, un enlace pegado— llegaba
      // con la lista vacía, el deep-link se daba por consumido, y cuando los
      // avatares aparecían ya nadie iba a abrirlo.
      if (loading) return;
      openedIdRef.current = tid;
      (async () => {
        const res = await templateService.getTemplate(tid);
        if (res.success && res.data) openTemplate(res.data);
        else error('Not Found', 'That avatar does not exist or you cannot see it.');
      })();
      return;
    }

    // The id left the URL (back button, or we cleared it) — drop the detail.
    if (!tid && openedIdRef.current) {
      openedIdRef.current = null;
      closeDetail();
    }
  }, [searchParams, templates, loading, creating, openTemplate, closeDetail, error]);

  // Filtrado de avatares (memoizado)
  // Filtrado del lado del cliente por tipo, búsqueda y tags
  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      // Filtro por tipo (all/public/mine)
      if (filter === "public" && !template.isPublic) return false;
      if (filter === "mine") {
        const isMine = template.accountId === accountId || template.account_id === accountId;
        if (!isMine) return false;
      }

      // Filtro por tags seleccionados (AND logic: debe tener todos los tags)
      if (selectedFilterTags.length > 0) {
        const templateTags = template.tags || [];
        const hasAllTags = selectedFilterTags.every(filterTag => 
          templateTags.some(tag => tag.toLowerCase() === filterTag.toLowerCase())
        );
        if (!hasAllTags) return false;
      }

      // Filtro por búsqueda de texto
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = template.name?.toLowerCase().includes(query);
        const matchesDescription = template.description?.toLowerCase().includes(query);
        const matchesTags = template.tags?.some(tag => tag.toLowerCase().includes(query));
        
        if (!matchesName && !matchesDescription && !matchesTags) {
          return false;
        }
      }

      return true;
    });
  }, [templates, filter, searchQuery, accountId, selectedFilterTags]);

  // Summary counts for the stats strip. Derived from the full `templates` list
  // (not the filtered one) so the numbers stay stable while filtering.
  const stats = useMemo(() => {
    const isMine = (t) => t.accountId === accountId || t.account_id === accountId;
    const langCounts = {};
    templates.forEach((t) => {
      const code = (t.language || "es").toLowerCase();
      langCounts[code] = (langCounts[code] || 0) + 1;
    });
    const topLang = Object.entries(langCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    return {
      total: templates.length,
      publicCount: templates.filter((t) => t.isPublic).length,
      mineCount: templates.filter(isMine).length,
      jsonCount: templates.filter((t) => outputFormatOf(t) === "JSON").length,
      topLang,
    };
  }, [templates, accountId]);

  useEffect(() => {
    if (currentAccount?.id) {
      // Clear current templates to avoid showing resources from previous context
      setTemplates([]);
      // Reload templates for the new account context
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount?.id]);


  useEffect(() => {
    const fetchProjects = async () => {
      setProjectsError('');
      try {
        // Use currentAccount.id from OrganizationContext instead of user.account_id
        const currentAccountId = currentAccount?.id;
        if (!currentAccountId) {
          setProjectsError('No se encontró el account_id del contexto actual.');
          return;
        }
        const token = TokenService.getAccessToken();
        if (!token || token === 'null' || token === 'undefined') {
          setProjectsError('Token de autenticación no encontrado o inválido. Por favor inicia sesión.');
          return;
        }
        const params = new URLSearchParams({
          account_id: currentAccountId,
          page: '1',
          page_size: '10'
        });
        const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL || process.env.REACT_APP_API_BASE_URL}/projects?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.status === 401) {
          setProjectsError('Token inválido o expirado. Por favor inicia sesión nuevamente.');
          setProjects([]);
        } else if (response.ok) {
          const data = await response.json();
          setProjects((data.body && data.body.projects) || []);
        } else {
          setProjectsError('Error al obtener los proyectos.');
        }
      } catch (error) {
        setProjectsError('Error al obtener los proyectos.');
        console.error('Error fetching projects:', error);
      }
    };
    // Use currentAccount.id instead of accountId
    if (currentAccount?.id) fetchProjects();
  }, [currentAccount?.id]);

  /**
   * Validar formulario y mostrar errores visuales
   */
  // An avatar exists as soon as it has a name (and, while private, a project to
  // live in). Blocks are written afterwards, inside the avatar — demanding one
  // up front is what forced a four-step wizard onto something this small, and
  // the backend never asked for it: `sections` defaults to empty.
  const validateForm = () => {
    const errors = {
      name: !name.trim(),
      projects: !isPublic && selectedProjects.length === 0
    };

    setValidationErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  /**
   * Limpiar errores de validación cuando se modifican los campos
   */
  const clearValidationError = (field) => {
    setValidationErrors(prev => ({ ...prev, [field]: false }));
  };

  useEffect(() => {
    if (editing && editingTemplate && !editingTemplate.isPublic) {
      // Si el template es privado, cargar los proyectos compartidos
      if (Array.isArray(editingTemplate.projectId)) {
        setSelectedProjects(editingTemplate.projectId);
      } else if (editingTemplate.projectId) {
        setSelectedProjects([editingTemplate.projectId]);
      } else {
        setSelectedProjects([]);
      }
    }
  }, [editing, editingTemplate]);

  /**
   * Cargar lista de templates
   * Usa listAccountTemplates para obtener templates públicos + privados de esta cuenta
   */
  /** Fetch one page. Throws on failure so callers can toast a single message. */
  const fetchTemplatesPage = useCallback(async (offset) => {
    const result = await templateService.listAccountTemplates(accountId, {
      limit: PAGE_SIZE,
      offset,
      view: 'summary',
    });
    if (!result.success) {
      throw new Error(result.error?.message || 'Failed to load avatars');
    }
    return (result.data || []).map((t) => ({
      ...t,
      avatar: t.avatar || "",
      tags: t.tags || [],
    }));
  }, [accountId]);

  /** Reset to the first page. Called on mount and after create/update/delete. */
  const loadTemplates = async () => {
    if (loadTemplatesInFlightRef.current) {
      return loadTemplatesInFlightRef.current;
    }
    setLoading(true);
    const requestPromise = (async () => {
    try {
      if (!accountId) {
        console.warn('⚠️ No accountId available, skipping template load');
        setLoading(false);
        return;
      }

      const page = await fetchTemplatesPage(0);
      setTemplates(page);
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      // NOTE: this used to be `catch (error)`, which shadowed the toast helper
      // of the same name and made the error handler itself throw.
      console.error('Error cargando templates:', err);
      error('Load Failed', err.message || 'Failed to load avatars');
    } finally {
      setLoading(false);
      loadTemplatesInFlightRef.current = null;
    }
    })();
    loadTemplatesInFlightRef.current = requestPromise;
    return requestPromise;
  };

  /** Append the next page. Guarded so overlapping scroll events fire once. */
  const loadMoreTemplates = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || loading || !accountId) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const page = await fetchTemplatesPage(templates.length);
      setTemplates((prev) => {
        // A concurrent reload could have replaced the list — drop duplicates.
        const seen = new Set(prev.map((t) => t.id));
        return [...prev, ...page.filter((t) => !seen.has(t.id))];
      });
      setHasMore(page.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error cargando más templates:', err);
      error('Load Failed', err.message || 'Failed to load more avatars');
      setHasMore(false); // stop retrying on every scroll tick
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [accountId, hasMore, loading, templates.length, fetchTemplatesPage, error]);

  // Infinite scroll: the page (not an inner div) is the scroll container here,
  // so the viewport is the observer root. rootMargin prefetches before the
  // sentinel is actually visible.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreTemplates();
      },
      { rootMargin: '400px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMoreTemplates]);

  /**
   * Crear nuevo template o actualizar existente
   */
  const handleCreateTemplate = async () => {
    
    // Validar formulario antes de proceder
    if (!validateForm()) {
      error('Validation Error', 'Please fix the highlighted fields before continuing.');
      return;
    }
    
    setSubmitting(true);
    try {
      // Validar JSON schema si el modo es json
      let finalJsonSchema = null;
      if (outputFormat === 'json') {
        if (jsonSchemaBuilderMode) {
          // Usar fields para generar schema
          if (jsonSchemaFields.length > 0) {
            finalJsonSchema = fieldsToJsonSchema(jsonSchemaFields);
          }
        } else {
          // Usar JSON manual
          if (jsonSchema && jsonSchema.trim()) {
            try {
              finalJsonSchema = JSON.parse(jsonSchema);
            } catch (jsonError) {
              error('Invalid JSON Schema', 'The JSON schema is not valid. Please check the syntax.');
              setSubmitting(false);
              return;
            }
          }
        }
      }
      
      // Blocks and examples go back as one array, sharing an order counter.
      const allSections = toSectionsPayload(sections, examples);

      const templateData = {
        name: name.trim(),
        description: description.trim(),
        is_default: isDefault,
        is_public: isPublic,
        avatar: (avatarBase64 && avatarBase64.trim() !== '') ? avatarBase64 : (avatarUrl && avatarUrl.trim() !== '') ? avatarUrl : null,
        tags: tags.length > 0 ? tags : [],
        sections: allSections,
        language: language,
        output_format: {
          mode: outputFormat,
          strict_validation: true,
          pretty_print: false,
          indent: 2,
          provider_params: {},
          ...(finalJsonSchema ? { json_schema: finalJsonSchema } : {})
        }
      };

      if (!editing) {
        templateData.account_id = accountId;
        templateData.project_id = isPublic ? undefined : (selectedProjects.length > 0 ? selectedProjects[0] : undefined);
      } else {
        templateData.project_id = isPublic ? undefined : (selectedProjects.length > 0 ? selectedProjects[0] : projectId);
      }
      
      let result;
      if (editing && editingTemplate) {
        result = await templateService.updateTemplate(editingTemplate.id, templateData, accountId);
      } else {
        result = await templateService.createTemplate(templateData);
      }
      if (result.success) {
        const actionMessage = editing ? 'updated' : 'created';
        success('Template Saved', `${name} ${actionMessage} successfully.`);

        if (!editing && onTemplateCreated) {
          await onTemplateCreated(result.data);
          return;
        }

        if (editing) {
          setIsEditModeActive(false);
          loadTemplates();
          return;
        }

        // Straight into the avatar that was just made, rather than back to the
        // list to hunt for it: everything left to fill in lives in there.
        setCreating(false);
        setEditing(false);
        resetForm();
        loadTemplates();
        if (result.data?.id) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('templateId', result.data.id);
            return next;
          });
        }
      } else {
        error('Save Failed', result.error.message || 'No se pudo guardar el template.');
      }
    } catch (err) {
      console.error('Unexpected Error:', err);
      error('Unexpected Error', 'An error occurred while saving the template.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Manejar eliminación de template
   */
  const handleDelete = useCallback((templateId) => {
    setTemplateToDelete(templateId);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    // Without an account we cannot authorize the delete; say so instead of
    // closing the dialog as if nothing happened.
    if (!accountId) {
      error('Cannot Delete', 'No account selected. Pick an organization or personal account first.');
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
      return;
    }

    const result = await templateService.deleteTemplate(templateToDelete, accountId);

    if (result.success) {
      success('Deleted', 'Template deleted successfully');
      // Deleting the avatar you have open would otherwise leave you staring at
      // a detail view for something that no longer exists.
      if (editing && editingTemplate?.id === templateToDelete) {
        handleBack();
      }
      loadTemplates(); // Recargar lista
    } else {
      console.error('❌ Error eliminando template:', result.error);
      
      // Detectar si es un error de permisos (no puede eliminar template público)
      const errorMessage = result.error?.message || '';
      const statusCode = result.error?.status || result.error?.statusCode;
      
      if (statusCode === 403 || errorMessage.toLowerCase().includes('permission') || 
          errorMessage.toLowerCase().includes('forbidden') || 
          errorMessage.toLowerCase().includes('not allowed')) {
        error('Cannot Delete', 'You cannot delete public templates. Only your own private templates can be deleted.');
      } else {
        error('Deletion Failed', errorMessage || 'Failed to delete template');
      }
    }
    
    setShowDeleteConfirm(false);
    setTemplateToDelete(null);
  };

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setTemplateToDelete(null);
  }, []);

  /**
   * Resetear completamente el formulario
   */
  const resetForm = () => {
    setName("");
    setDescription("");
    setIsDefault(false);
    setIsPublic(false);
    setSections([]);
    setExamples([]);
    setTags([]);
    setTagInput("");
    setSelectedProjects([]);
    setAvatarBase64("");
    setAvatarUrl("");
    setLanguage("es");
    setOutputFormat("normal");
    setJsonSchema("");
    setJsonSchemaFields([]);
    setEditingTemplate(null);
    // Limpiar errores de validación
    setValidationErrors({
      name: false,
      sections: false,
      projects: false
    });
  };

  /** Drop the open avatar from the URL. Leaves the rest of the query intact. */
  const clearTemplateIdFromUrl = useCallback(() => {
    openedIdRef.current = null;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('templateId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /**
   * Salir del modo creación/edición
   */
  const handleBack = () => {
    const shouldReturnToAssistantWizard = Boolean(creating && onBackToAssistantCreate);

    window.scrollTo(0, 0);
    setCreating(false);
    setEditing(false);
    setIsEditModeActive(false);
    // Reset completo del formulario
    resetForm();
    clearTemplateIdFromUrl();

    if (shouldReturnToAssistantWizard) {
      onBackToAssistantCreate();
    }
  };

  useEffect(() => {
    if (!launchCreateMode) return;

    resetForm();
    setEditing(false);
    setIsEditModeActive(true);
    setCreating(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchCreateMode]);

  /**
   * Duplicar el avatar actual
   */
  const handleDuplicateAvatar = () => {
    if (!editingTemplate) return;
    
    // Copiar datos del avatar actual
    setName(`Copy of ${name || editingTemplate.name}`);
    // description, sections, examples, tags ya están en el estado
    setIsPublic(false); // El duplicado siempre empieza como privado
    setIsDefault(false); // El duplicado nunca es default
    
    // Cambiar a modo creación con los datos pre-llenados
    setEditing(false);
    setEditingTemplate(null);
    setCreating(true);
    setIsEditModeActive(true);
    // The copy is a new avatar — the URL should stop pointing at the original.
    clearTemplateIdFromUrl();

    success('Ready to Create', 'Avatar duplicated. Make your changes and save.');
  };

  /**
   * Calcular tags más usadas en todos los templates
   */
  const getSuggestedTags = () => {
    const tagCounts = {};
    templates.forEach(template => {
      if (template.tags && Array.isArray(template.tags)) {
        template.tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });
    
    // Ordenar por uso y retornar top 8
    return Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag]) => tag)
      .filter(tag => !tags.includes(tag)); // Excluir tags ya agregadas
  };

  /**
   * Calcular trending tags con sus conteos para mostrar en el filtro (memoizado)
   */
  const getTrendingTags = useMemo(() => {
    const tagCounts = {};
    templates.forEach(template => {
      if (template.tags && Array.isArray(template.tags)) {
        template.tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });
    
    // Ordenar por uso y retornar top 10 con sus conteos
    return Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([tag, count]) => ({ tag, count }));
  }, [templates]);

  /**
   * Agregar o remover tag del filtro (memoizado)
   */
  const toggleFilterTag = useCallback((tag) => {
    if (selectedFilterTags.includes(tag)) {
      setSelectedFilterTags(selectedFilterTags.filter(t => t !== tag));
    } else {
      setSelectedFilterTags([...selectedFilterTags, tag]);
    }
  }, [selectedFilterTags]);

  /**
   * Limpiar todos los filtros
   */
  const clearAllFilters = useCallback(() => {
    setSearchQuery("");
    setSelectedFilterTags([]);
  }, []);

  /**
   * Agregar tag (memoizado)
   */
  const handleAddTag = useCallback((tag) => {
    const normalized = normalizeTag(tag);
    if (normalized && !tags.includes(normalized)) {
      setTags([...tags, normalized]);
      setTagInput("");
    }
  }, [tags]);

  /**
   * Remover tag (memoizado)
   */
  const handleRemoveTag = useCallback((tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  }, [tags]);

  /**
   * Manejar input de tag (Enter o coma para agregar)
   */
  /**
   * Manejar input de tag (Enter o coma para agregar) (memoizado)
   */
  const handleTagInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (tagInput.trim()) {
        handleAddTag(tagInput);
      }
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      // Si está vacío y presiona backspace, quitar último tag
      handleRemoveTag(tags[tags.length - 1]);
    }
  }, [tagInput, tags, handleAddTag, handleRemoveTag]);

  // Si está cargando
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center space-x-3 text-white">
          <Loader className="w-5 h-5 animate-spin" />
          <span>Loading templates...</span>
        </div>
      </div>
    );
  }

  const isOwner = editing && editingTemplate
    ? (editingTemplate.accountId === accountId || editingTemplate.account_id === accountId)
    : true; // Si está creando, siempre es propio

  // Detalle: una vista con tabs. Editar ocurre dentro de ella, no en otra pantalla.
  if (editing && !creating) {
    const form = {
      name, setName,
      description, setDescription,
      tags, setTags,
      tagInput, setTagInput,
      handleAddTag, handleRemoveTag, handleTagInputKeyDown, getSuggestedTags,
      avatarUrl, setAvatarUrl,
      avatarBase64, setAvatarBase64,
      language, setLanguage,
      outputFormat, setOutputFormat,
      jsonSchema, setJsonSchema,
      jsonSchemaFields, setJsonSchemaFields,
      jsonSchemaBuilderMode, setJsonSchemaBuilderMode,
      sections, setSections,
      examples, setExamples,
      isPublic, setIsPublic,
      projects, projectsError,
      selectedProjects, setSelectedProjects,
      validationErrors, clearValidationError,
      onImageError: (msg) => error('Avatar', msg),
    };

    return (
      <>
        <AvatarDetail
          template={editingTemplate}
          form={form}
          isOwner={isOwner}
          isEditing={isEditModeActive}
          submitting={submitting}
          onBack={handleBack}
          onEdit={() => setIsEditModeActive(true)}
          onCancelEdit={() => {
            setIsEditModeActive(false);
            // Discard edits by re-reading the avatar from the server.
            if (editingTemplate) openTemplate(editingTemplate);
          }}
          onSave={handleCreateTemplate}
          onDuplicate={handleDuplicateAvatar}
          onDelete={handleDelete}
          onCopyId={handleCopyId}
          onCopyText={handleCopyText}
          onOpenEntry={(entry, index, type) => setExpandedSection({ ...entry, type, index })}
        />

        <SectionModal entry={expandedSection} onClose={() => setExpandedSection(null)} />
        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete Template"
          message="This action cannot be undone. Are you sure you want to delete this template?"
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      </>
    );
  }


  // Vista principal - Lista de templates
  return (
    <div className="h-full flex flex-col opacity-0 animate-blur-reveal [animation-fill-mode:forwards]">
      {/* Toolbar: title · search · filters · view mode · create */}
      <div
        className="mb-4 flex flex-col gap-4 opacity-0 animate-reveal-down [animation-fill-mode:forwards] xl:flex-row xl:items-center xl:justify-between"
        style={{ animationDelay: '0.05s' }}
      >
        {/* Title — lo omite quien vive dentro de una sección con pestañas. */}
        {!hideTitle && (
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">Avatars</h1>
            <p className="text-xs text-gray-400 sm:text-sm">
              Create and manage AI avatars that represent your brand and assist your audience.
            </p>
          </div>
        )}

        {/* Search */}
        <div className="relative flex-1 xl:max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search avatars by name, description, or tags..."
            className="w-full rounded-xl border border-white/10 bg-black/40 py-2.5 pl-10 pr-10 text-sm text-white placeholder-gray-500 backdrop-blur-sm transition-colors focus:border-violet-500/50 focus:outline-none"
          />
          {(searchQuery || selectedFilterTags.length > 0) && (
            <button
              onClick={clearAllFilters}
              aria-label="Clear filters"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-white"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filters · view mode · create — one row. Wrapping stacked them into
            the pile of loose buttons this looked like on a phone. */}
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 rounded-lg border border-white/10 bg-black/50 p-1 xl:flex-none">
            {/* El número va en la pastilla. Estaba abajo, en una franja aparte
                que repetía estos mismos tres —Total, Public, Mine— como si
                fueran otro dato: dos filas para decir lo mismo, en una pantalla
                donde la primera tarjeta ya empezaba fuera de vista. */}
            {[
              { key: "all", label: "All", count: stats.total },
              { key: "public", label: "Public", count: stats.publicCount },
              { key: "mine", label: "My Avatars", shortLabel: "Mine", count: stats.mineCount },
            ].map(({ key, label, shortLabel, count }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`flex min-w-0 flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 text-xs transition-colors sm:gap-1.5 sm:px-3 sm:text-sm xl:flex-none ${
                  filter === key ? "bg-primary text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                <span className={shortLabel ? "hidden sm:inline" : ""}>{label}</span>
                {shortLabel && <span className="sm:hidden">{shortLabel}</span>}
                <span
                  className={`shrink-0 rounded-full px-1 py-0.5 text-[10px] tabular-nums sm:px-1.5 ${
                    filter === key ? "bg-white/20 text-white" : "bg-white/10 text-gray-400"
                  }`}
                >
                  {count}
                  {hasMore && <span className="opacity-60">+</span>}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-shrink-0 rounded-lg border border-white/10 bg-black/50 p-1">
            <button
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
              className={`rounded-md p-1.5 transition-colors sm:p-2 ${
                viewMode === "grid" ? "bg-white/15 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              aria-label="List view"
              className={`rounded-md p-1.5 transition-colors sm:p-2 ${
                viewMode === "list" ? "bg-white/15 text-white" : "text-gray-400 hover:text-white"
              }`}
            >
              <LayoutList className="h-4 w-4" />
            </button>
          </div>

          {/* Icon-only on phones — the label would eat the whole toolbar row. */}
          <Button
            onClick={() => {
              resetForm();
              setCreating(true);
              setIsEditModeActive(true);
            }}
            aria-label="Create Avatar"
            title="Create Avatar"
            className="h-9 w-9 flex-shrink-0 p-0 text-sm bg-primary text-white transition-colors hover:bg-violet-700 sm:h-10 sm:w-auto sm:px-5"
          >
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Create Avatar</span>
          </Button>
        </div>
      </div>

      {/* Stats strip — sólo de `sm` para arriba.
          En un teléfono era la quinta fila de cromo seguida, y tres de sus
          cinco números —Total, Public, Mine— son los de los filtros de arriba.
          Los otros dos, JSON y Lang, son agregados que nadie viene a leer aquí
          y que además se leen por avatar en su propio subtítulo. */}
      <div
        className="mb-4 hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 opacity-0 animate-reveal-down [animation-fill-mode:forwards] sm:block sm:p-4"
        style={{ animationDelay: '0.08s' }}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <StatTile Icon={Sparkles} tone="pink" label="Total Avatars" value={stats.total} sub="Across all projects" approx={hasMore} />
          <StatTile Icon={Globe} label="Public" value={stats.publicCount} sub="Visible to everyone" approx={hasMore} />
          <StatTile Icon={Users} tone="pink" label="My Avatars" value={stats.mineCount} sub="Created by you" approx={hasMore} />
          <StatTile Icon={Code2} label="JSON Output" value={stats.jsonCount} sub="Structured responses" approx={hasMore} />
          <StatTile
            Icon={Languages}
            label="Language"
            value={stats.topLang ? stats.topLang.toUpperCase() : "—"}
            sub={stats.topLang ? getTemplateLanguageLabel(stats.topLang) : "No avatars yet"}
          />
        </div>
      </div>

      {/* Trending tags + active filters */}
      <div className="mb-6 space-y-4 opacity-0 animate-reveal-down [animation-fill-mode:forwards]" style={{ animationDelay: '0.1s' }}>

        {/* Trending Tags */}
        {showTrendingTags && getTrendingTags.length > 0 && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 sm:p-5">
            <div className="flex items-center justify-between mb-2 sm:mb-4">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-purple-400" />
                <h3 className="text-sm font-medium text-purple-300">Trending Tags</h3>
                <span className="text-xs text-gray-500">({getTrendingTags.length})</span>
              </div>
              <button
                onClick={() => setShowTrendingTags(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {getTrendingTags.map(({ tag, count }, index) => {
                const isSelected = selectedFilterTags.includes(tag);
                // Ten tags is three rows on a phone. Only the top five show
                // there — but never hide one the user is filtering by.
                const hiddenOnPhone = index >= 5 && !isSelected;
                return (
                  <button
                    key={tag}
                    onClick={() => toggleFilterTag(tag)}
                    className={`
                      rounded-md px-2 py-0.5 text-xs font-medium sm:rounded-lg sm:px-3 sm:py-1.5 sm:text-sm
                      transition-all duration-200
                      ${hiddenOnPhone ? 'hidden sm:inline-block' : ''}
                      ${
                        isSelected
                          ? 'bg-primary text-white'
                          : 'bg-white/10 text-purple-300 hover:bg-white/15'
                      }
                    `}
                  >
                    <span className="mr-1.5">{tag}</span>
                    <span className={`text-xs ${
                      isSelected ? 'text-purple-200' : 'text-gray-400'
                    }`}>({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filtros activos */}
        {selectedFilterTags.length > 0 && (
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <div className="flex items-center space-x-2 px-3 py-1 bg-purple-600/20 rounded-lg border border-purple-500/30">
              <Filter className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300">{selectedFilterTags.length} active</span>
            </div>
            {selectedFilterTags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1.5 bg-purple-600/30 border border-purple-500/40 text-purple-200 rounded-lg text-sm"
              >
                {tag}
                <button
                  onClick={() => toggleFilterTag(tag)}
                  className="ml-2 hover:text-white transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="overflow-y-auto opacity-0 animate-reveal-up [animation-fill-mode:forwards]" style={{ animationDelay: '0.2s' }}>
        {filteredTemplates.length === 0 ? (
          // Empty State
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mb-4">
              <Image className="w-8 h-8 text-purple-400" />
            </div>
            <h3 className="text-white text-lg mb-2">No Avatars Yet</h3>
            <p className="text-gray-400 mb-6 max-w-md">
              Create your first avatar to define the personality and behavior patterns for your agents.
            </p>
          </div>
        ) : (
          // Templates Grid/List. Two per row on a phone — one full-width card
          // meant one avatar per screen, which is a gallery you have to scroll
          // to browse.
          //
          // One column more at each wide breakpoint than the 16:10 cover needed:
          // a square cover is as tall as the card is wide, so the column count
          // that read fine before turns every card into a poster.
          <div className={viewMode === "grid" ? "grid grid-cols-3 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6" : "space-y-2"}>
            {filteredTemplates.map((template) => (
              <MagicHoverWrapper key={template.id}>
                <AvatarCard
                  template={template}
                  viewMode={viewMode}
                  onSelect={handleSelectTemplate}
                  onDelete={handleDelete}
                  onCopyId={handleCopyId}
                />
              </MagicHoverWrapper>
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel — observed to pull the next page. */}
        {hasMore && (
          <div ref={sentinelRef} className="flex items-center justify-center py-8">
            {loadingMore && (
              <span className="flex items-center gap-2 text-sm text-gray-400">
                <Loader className="h-4 w-4 animate-spin" />
                Loading more avatars…
              </span>
            )}
          </div>
        )}
      </div>

      {creating && (
        <CreateAvatarDialog
          accountId={accountId}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          image={avatarBase64 || avatarUrl}
          onImageChange={(dataUrl) => {
            setAvatarBase64(dataUrl);
            setAvatarUrl("");
          }}
          onImageError={(msg) => error('Avatar', msg)}
          isPublic={isPublic}
          setIsPublic={setIsPublic}
          projects={projects}
          projectsError={projectsError}
          selectedProjects={selectedProjects}
          setSelectedProjects={setSelectedProjects}
          validationErrors={validationErrors}
          clearValidationError={clearValidationError}
          submitting={submitting}
          onCancel={handleBack}
          onSubmit={handleCreateTemplate}
        />
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Template"
        message="This action cannot be undone. Are you sure you want to delete this template?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}
